import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminManageProducts.css';
import { useCategoryManagement } from '../../hooks/useCategoryManagement';
import { useProductManagement } from '../../hooks/useProductManagement';
import { uploadToFirebaseStorage, PRESET_PRODUCT } from '../../utils/firebaseStorage';
import { buildCategoryTree } from '../../firebase/categories';
import { getAllAttributes, getAttributesForProduct } from '../../firebase/attributes';
import { getTotalStock } from '../../firebase/products';
import ProductAttributeFields from '../ProductAttributeFields/ProductAttributeFields';

// ─── Size constants ───────────────────────────────────────────────────────────
const ADULT_SIZES_DISPLAY = ['XXS','XS','Small','Medium','Large','XL','2XL','3XL','4XL','5XL','6XL'];
const KIDS_SIZES_DISPLAY  = ['XS','Small','Medium','Large','XL','2XL'];
const ADULT_SIZES_KEYS    = ['xxs','xs','small','medium','large','xl','xxl','xxxl','xxxxl','xxxxxl','xxxxxxl'];
const KIDS_SIZES_KEYS     = ['xs','small','medium','large','xl','xxl'];
const SIZE_DISPLAY_TO_KEY = {
    'XXS': 'xxs',
    'XS': 'xs',
    'Small': 'small',
    'Medium': 'medium',
    'Large': 'large',
    'XL': 'xl',
    '2XL': 'xxl',
    '3XL': 'xxxl',
    '4XL': 'xxxxl',
    '5XL': 'xxxxxl',
    '6XL': 'xxxxxxl',
};

function normalizeSizePricesForForm(sizePrices) {
    if (!sizePrices || typeof sizePrices !== 'object' || Array.isArray(sizePrices)) return {};
    return Object.entries(sizePrices).reduce((acc, [key, value]) => {
        const normalized = SIZE_DISPLAY_TO_KEY[key] || key;
        acc[normalized] = value;
        return acc;
    }, {});
}

const COLOR_PRESETS = ['White','Black','Red','Blue','Green','Navy','Gray','Yellow','Orange','Pink','Purple','Brown'];
const PAGE_SIZE_OPTIONS = [10,15,20,25];
const COLOR_MAP = {
    White:'#f5f5f5',Black:'#1f1f1f',Red:'#e53935',Blue:'#1a5faa',
    Green:'#2e7d32',Navy:'#1a237e',Gray:'#888',Yellow:'#f9a825',
    Orange:'#e65100',Pink:'#e91e8c',Purple:'#7b1fa2',Brown:'#5d4037',
};

const NO_COLOR_SUBCATEGORIES = ['Jersey', 'Polo Shirt'];
const CLOTHING_CATEGORY      = 'Clothing';
const TSHIRT_MAX_PRICE_WARN  = 150;

// ─── Tab bar ──────────────────────────────────────────────────────────────────
function ProductTabBar({ active }) {
    const navigate = useNavigate();
    const tabs = [
        { label:'Manage Products',   path:'/admin/products/manage' },
        { label:'Manage Category', path:'/admin/products/categories' },
        { label:'Manage Subcategory', path:'/admin/products/subcategories' },
        { label:'Manage Submenus', path:'/admin/products/submenus' },
        { label:'Manage Attributes', path:'/admin/products/attributes' },
        { label:'Manage Inventory',  path:'/admin/products/inventory' },
    ];
    return (
        <div className="mp-tab-bar">
            {tabs.map(t => (
                <button key={t.path} className={`mp-tab${active===t.label?' mp-tab--active':''}`} onClick={() => navigate(t.path)}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

function StatusBadge({ status }) {
    const cfg = status === 'active'
        ? { bg:'#e6f4e6', color:'#2e7d32', label:'Active' }
        : { bg:'#fdecea', color:'#c62828', label:'Inactive' };
    return <span className="mp-status-badge" style={{ background:cfg.bg, color:cfg.color }}>{cfg.label}</span>;
}

function ColorDot({ color }) {
    return <span className="mp-color-dot" style={{ background:COLOR_MAP[color]||'#ccc', border:color==='White'?'1px solid #ddd':'none' }} title={color} />;
}

// ─── Render dynamic attributes for non-Clothing products ─────────────────────
function DynamicAttributesDisplay({ attributes }) {
    if (!attributes || typeof attributes !== 'object' || Object.keys(attributes).length === 0) {
        return <span className="mp-no-colors">—</span>;
    }
    return (
        <div className="mp-attrs-cell">
            {Object.entries(attributes).map(([key, value]) => {
                const val = typeof value === 'object' && value !== null ? value.value : value;
                return (
                    <span key={key} className="mp-attr-chip" title={`${key}: ${val}`}>
                        {key}: {val}
                    </span>
                );
            })}
        </div>
    );
}

function ImageSlot({ index, preview, onFileChange, onRemove, isMain }) {
    return (
        <div className={`mp-img-slot${isMain?' mp-img-slot--main':''}`}>
            {isMain && <span className="mp-img-slot-badge">Main</span>}
            {preview ? (
                <>
                    <img src={preview} alt={`Product ${index+1}`} className="mp-img-slot-preview" />
                    <div className="mp-img-slot-actions">
                        <label className="mp-img-slot-btn">
                            <input type="file" accept="image/*" onChange={e => onFileChange(index, e.target.files[0])} hidden />
                            Change
                        </label>
                        <button type="button" className="mp-img-slot-btn mp-img-slot-btn--remove" onClick={() => onRemove(index)}>✕</button>
                    </div>
                </>
            ) : (
                <label className="mp-img-slot-empty">
                    <input type="file" accept="image/*" onChange={e => onFileChange(index, e.target.files[0])} hidden />
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    <span>{isMain ? 'Main Image' : `Image ${index+1}`}</span>
                </label>
            )}
        </div>
    );
}

// ─── Product Modal ────────────────────────────────────────────────────────────
function ProductModal({ product, onClose, onSave, categories, subcategoryMap, subcategoryEntities, attributes }) {
    const isEdit = !!product;

    const [form, setForm] = useState({
        name:           product?.name           || '',
        category:       product?.category       || '',
        subcategory:    product?.subcategory    || '',
        price:          product?.price          || '',
        stockQuantity:  product?.stockQuantity !== undefined ? product.stockQuantity : '',
        status:         product?.status         || 'active',
        sizeTier:       product?.sizeTier       || '',
        colors:         product?.colors         || [],
        availableSizes: product?.availableSizes || [],
        sizePrices:     normalizeSizePricesForForm(product?.sizePrices || {}),
        colorPrices:    product?.colorPrices    || {},
        description:    product?.description    || '',
        submenuTags:    Array.isArray(product?.submenuTags) ? product.submenuTags : [],
    });

    const [, setColorPriceFocused] = useState({});
    const [dynamicAttributes, setDynamicAttributes] = useState(() => {
        const existing = product?.selectedAttributes || product?.attributes;
        return existing && typeof existing === 'object' ? { ...existing } : {};
    });
    const [imageFiles,    setImageFiles]    = useState([null,null,null,null]);
    const [imagePreviews, setImagePreviews] = useState(() => {
        const existing = Array.isArray(product?.images) ? product.images : [];
        const main     = product?.imageUrl || product?.image || '';
        return [main, existing[1]||'', existing[2]||'', existing[3]||''];
    });
    const [colorInput,  setColorInput]  = useState('');
    const [uploading,   setUploading]   = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const subcategoryOptions = subcategoryMap[form.category] || [];
    const selectedSubcategory = subcategoryEntities.find((subcategory) => subcategory.name === form.subcategory && subcategory.parentName === form.category)
        || subcategoryEntities.find((subcategory) => subcategory.name === form.subcategory);
    const assignedAttributes = useMemo(() => {
        return getAttributesForProduct(attributes, form.category, null, selectedSubcategory);
    }, [attributes, form.category, selectedSubcategory]);

    const prevCategoryRef = React.useRef({ category: form.category, subcategory: form.subcategory });
    useEffect(() => {
        const prev = prevCategoryRef.current;
        if (prev.category !== form.category || prev.subcategory !== form.subcategory) {
            if (!isEdit) setDynamicAttributes({});
            prevCategoryRef.current = { category: form.category, subcategory: form.subcategory };
        }
    }, [form.category, form.subcategory, isEdit]);

    // Auto-cleanup for dynamic attributes was removed here at user request.

    const hasColorToggle     = assignedAttributes.some((attr) => attr.type === 'color');

    // Clear clothing-specific fields when switching away from Clothing (and no color toggle is active)
    useEffect(() => {
        if (form.category === CLOTHING_CATEGORY || hasColorToggle) return;
        setForm((prev) => {
            if (!prev.sizeTier && (prev.availableSizes || []).length === 0 && (prev.colors || []).length === 0) return prev;
            return {
                ...prev,
                sizeTier: '',
                availableSizes: [],
                sizePrices: {},
                colors: [],
                colorPrices: {},
            };
        });
    }, [form.category, hasColorToggle]);

    const isClothing         = form.category === CLOTHING_CATEGORY;
    // Variant fields (Size Tier, Colors, Per-Size Pricing) show when Clothing category is selected OR when a Color Toggle attribute exists
    const showVariantFields  = isClothing || hasColorToggle;
    const showColors         = (isClothing && form.subcategory && !NO_COLOR_SUBCATEGORIES.includes(form.subcategory)) || hasColorToggle;
    const isTShirt           = form.subcategory === 'T-Shirt';
    const isJersey           = form.subcategory === 'Jersey';
    const isPoloShirt        = form.subcategory === 'Polo Shirt';
    const sizesDisplay       = form.sizeTier === 'Kids' ? KIDS_SIZES_DISPLAY : ADULT_SIZES_DISPLAY;
    const sizesKeys          = form.sizeTier === 'Kids' ? KIDS_SIZES_KEYS    : ADULT_SIZES_KEYS;
    const hasSizePricing     = Object.values(form.sizePrices || {}).some(
        (value) => value !== null && value !== undefined && value !== ''
    );
    const isSimpleProduct    = !form.sizeTier
        && form.colors.length === 0
        && form.availableSizes.length === 0
        && !hasSizePricing
        && Object.keys(dynamicAttributes).length === 0;

    const priceWarnings = useMemo(() => {
        if (!isTShirt) return [];
        return Object.entries(form.sizePrices)
            .filter(([k,v]) => (form.availableSizes||[]).includes(k) && Number(v) > TSHIRT_MAX_PRICE_WARN)
            .map(([k]) => { const idx = sizesKeys.indexOf(k); return idx>=0?sizesDisplay[idx]:k; });
    }, [form.sizePrices, form.availableSizes, isTShirt, sizesKeys, sizesDisplay]);

    const addColor = (c) => {
        const val = c || colorInput.trim();
        if (!val || form.colors.includes(val)) return;
        setForm(p => ({
            ...p,
            colors: [...p.colors, val],
            colorPrices: {
                ...p.colorPrices,
                [val]: val === 'White' ? 0 : (p.colorPrices[val] ?? ''),
            },
        }));
        setColorInput('');
    };
    const removeColor = (c) => {
        setForm(p => {
            const next = { ...p, colors:p.colors.filter(x => x!==c) };
            const cp   = { ...next.colorPrices }; delete cp[c];
            next.colorPrices = cp; return next;
        });
        setColorPriceFocused(prev => { const n={...prev}; delete n[c]; return n; });
    };
    const handleColorPriceFocus  = (cn) => setColorPriceFocused(prev => ({ ...prev, [cn]:true }));
    const handleColorPriceBlur   = (cn, val) => {
        setColorPriceFocused(prev => ({ ...prev, [cn]:false }));
        if (val===''||val===null||val===undefined)
            setForm(p => ({ ...p, colorPrices:{ ...p.colorPrices, [cn]:0 } }));
    };
    const handleColorPriceChange = (cn, val) => {
        setForm(p => ({ ...p, colorPrices:{ ...p.colorPrices, [cn]:val===''?'':Number(val) } }));
    };
    const getColorPriceDisplayValue = (cn) => {
        const raw = form.colorPrices[cn];
        return raw || '';
    };

    const handleImageFile   = (index, file) => {
        if (!file) return;
        const newFiles = [...imageFiles]; newFiles[index] = file; setImageFiles(newFiles);
        const reader = new FileReader();
        reader.onload = (ev) => { const p=[...imagePreviews]; p[index]=ev.target.result; setImagePreviews(p); };
        reader.readAsDataURL(file);
    };
    const handleImageRemove = (index) => {
        const f=[...imageFiles], p=[...imagePreviews];
        f[index]=null; p[index]=''; setImageFiles(f); setImagePreviews(p);
    };
    const toggleAvailableSize = (sizeKey) => {
        setForm(p => {
            const current = p.availableSizes || [];
            const next = current.includes(sizeKey) ? current.filter(s=>s!==sizeKey) : [...current,sizeKey];
            return { ...p, availableSizes:next };
        });
    };
    const handleSizePriceChange = (sizeKey, val) => {
        setForm(p => ({ ...p, sizePrices:{ ...p.sizePrices, [sizeKey]:val===''?null:Number(val) } }));
    };

    const handleSubmit = async () => {
    if (!form.name.trim() || !form.category) return;

    const stockQuantity = form.stockQuantity === '' || form.stockQuantity === null
        ? undefined
        : Number(form.stockQuantity);

    if (isSimpleProduct && form.stockQuantity !== '' && (!Number.isInteger(stockQuantity) || stockQuantity < 0)) {
        setUploadError('Please enter a valid non-negative whole-number stock quantity.');
        return;
    }

    // ── Validate main image ───────────────────────────────────────────────
    if (!imagePreviews[0]) {
        setUploadError('Please upload a main image for this product.');
        return;
    }

    // ── Validate base price ───────────────────────────────────────────────
    if (form.price === '' || form.price === null || Number(form.price) <= 0) {
        setUploadError('Please enter a valid base price greater than 0.');
        return;
    }

    // ── Validate size prices ──────────────────────────────────────────────
    const hasAnySizePricing = (form.availableSizes || []).length > 0
        || Object.keys(form.sizePrices || {}).some((key) => form.sizePrices[key] !== null && form.sizePrices[key] !== undefined && form.sizePrices[key] !== '');

    if (showVariantFields && !form.sizeTier && hasAnySizePricing) {
        setUploadError('Select a Size Tier before configuring available sizes and per-size pricing.');
        return;
    }

    if (showVariantFields && form.sizeTier) {
        const missingSizes = (form.availableSizes || []).filter(
            key => !form.sizePrices?.[key] || Number(form.sizePrices[key]) <= 0
        );
        if (form.availableSizes.length === 0) {
            setUploadError('Please select at least one available size.');
            return;
        }
        if (missingSizes.length > 0) {
            const labels = missingSizes.map(k => {
                const idx = sizesKeys.indexOf(k);
                return idx >= 0 ? sizesDisplay[idx] : k;
            });
            setUploadError(`Please set a price greater than 0 for: ${labels.join(', ')}.`);
            return;
        }
    }

    // ── Validate color surcharges ─────────────────────────────────────────
    if (showColors && form.colors.length === 0) {
        setUploadError('Please add at least one color for this product.');
        return;
    }
    if (showColors && form.colors.length > 0) {
        const missingColorPrices = form.colors.filter(
            c => form.colorPrices?.[c] === '' || form.colorPrices?.[c] === undefined || form.colorPrices?.[c] === null
        );
        if (missingColorPrices.length > 0) {
            setUploadError(`Please set a surcharge (₱0 or more) for: ${missingColorPrices.join(', ')}.`);
            return;
        }
        const nonWhiteZero = form.colors.filter(
            c => c !== 'White' && (Number(form.colorPrices?.[c]) <= 0 || form.colorPrices?.[c] === '')
        );
        if (nonWhiteZero.length > 0) {
            setUploadError(`Non-white colors must have a surcharge greater than ₱0: ${nonWhiteZero.join(', ')}.`);
            return;
        }
    }

        setUploading(true); setUploadError(null);
        const finalUrls = [...imagePreviews];

        // Upload all product images in parallel
        const uploadResults = await Promise.all(
            [0, 1, 2, 3].map(async (i) => {
                if (!imageFiles[i]) return { index: i, url: null, error: null };
                const { url, error } = await uploadToFirebaseStorage(imageFiles[i], 'jg-printing/products', PRESET_PRODUCT);
                return { index: i, url, error };
            })
        );

        for (const result of uploadResults) {
            if (result.error) {
                setUploadError(`Image ${result.index + 1} upload failed: ${result.error}`);
                setUploading(false);
                return;
            }
            if (result.url) finalUrls[result.index] = result.url;
        }

        setUploading(false);
        const imageUrl = finalUrls[0] || '';
        const images   = finalUrls.filter(Boolean);
        const description = form.description.trim();
        const selectedAttributes = Object.keys(dynamicAttributes).length > 0 ? { ...dynamicAttributes } : undefined;
        console.debug('[AdminManageProducts:ProductModal] submitting product form', {
            selectedAttributes,
            mode: isEdit ? 'edit' : 'create',
            productId: product?.id || null,
            hasDescription: Boolean(description),
            descriptionLength: description.length,
            descriptionPreview: description.slice(0, 80),
            hasStockQuantity: isSimpleProduct && stockQuantity !== undefined,
        });
        onSave({
            ...form,
            price:          Number(form.price) || 0,
            ...(isSimpleProduct && { stockQuantity }),
            sizeTier:       showVariantFields ? (form.sizeTier || null) : null,
            colors:         showColors ? form.colors      : [],
            colorPrices:    showColors ? form.colorPrices : {},
            availableSizes: showVariantFields ? form.availableSizes : undefined,
            sizePrices:     showVariantFields ? form.sizePrices     : undefined,
            description,
            submenuTags:    form.submenuTags,
            selectedAttributes,
            imageUrl, images,
        });
    };

    return (
        <div className="mp-modal-overlay" onClick={onClose}>
            <div className="mp-modal mp-modal--wide" onClick={e => e.stopPropagation()}>
                <div className="mp-modal-header">
                    <h2 className="mp-modal-title">{isEdit ? 'Edit Product' : 'Add New Product'}</h2>
                    <button className="mp-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="mp-modal-body mp-modal-body--wide">
                    {/* Left: images */}
                    <div className="mp-modal-left">
                        <div className="mp-field-label-section">Product Images</div>
                        <p className="mp-field-hint">Upload up to 4 images. The first is used as the catalog card preview.</p>
                        <div className="mp-img-slots">
                            {[0,1,2,3].map(i => (
                                <ImageSlot key={i} index={i} preview={imagePreviews[i]}
                                    onFileChange={handleImageFile} onRemove={handleImageRemove} isMain={i===0} />
                            ))}
                        </div>
                        {uploadError && <p className="mp-image-error">{uploadError}</p>}
                    </div>

                    {/* Right: fields */}
                    <div className="mp-modal-fields">
                        <div className="mp-field">
                            <label>Product Name <span className="mp-required">*</span></label>
                            <input type="text" value={form.name} onChange={e => setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Blue Corner T-Shirt" />
                        </div>
                        <div className="mp-field-row">
                            <div className="mp-field">
                                <label>Category <span className="mp-required">*</span></label>
                                <select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value,subcategory:''}))}>
                                    <option value="">Select category</option>
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="mp-field">
                                <label>Subcategory</label>
                                <select value={form.subcategory} onChange={e => setForm(p=>({...p,subcategory:e.target.value}))} disabled={!form.category}>
                                    <option value="">Select subcategory</option>
                                    {subcategoryOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        {/* Submenu Tags — shown when the selected subcategory has active submenus */}
                        {selectedSubcategory && Array.isArray(selectedSubcategory.submenus) && selectedSubcategory.submenus.filter((sm) => (sm.status || 'active') === 'active').length > 0 && (
                            <div className="mp-field">
                                <label>Submenu Tags <span className="mp-field-hint-inline">Select all submenus this product belongs to</span></label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 4 }}>
                                    {selectedSubcategory.submenus
                                        .filter((sm) => (sm.status || 'active') === 'active')
                                        .map((sm) => (
                                            <label key={sm.id || sm.name} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 'normal' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={form.submenuTags.includes(sm.name)}
                                                    onChange={(e) => {
                                                        setForm((p) => ({
                                                            ...p,
                                                            submenuTags: e.target.checked
                                                                ? [...p.submenuTags, sm.name]
                                                                : p.submenuTags.filter((t) => t !== sm.name),
                                                        }));
                                                    }}
                                                />
                                                {sm.name}
                                            </label>
                                        ))}
                                </div>
                            </div>
                        )}
<ProductAttributeFields
                        definitions={assignedAttributes.filter((a) => a.type !== 'color')}
                        selections={dynamicAttributes}
                        onChange={(name, value) => setDynamicAttributes((prev) => ({ ...prev, [name]: value }))}
                        prefix="mp"
                        fieldClassName="mp-field"
                        showSectionTitle={assignedAttributes.filter((a) => a.type !== 'color').length > 0}
                        sectionTitle="Attributes"
                        allowPriceInput={true}
                        forceCheckbox={true}
                    />
                        <div className="mp-field-row">
                            <div className="mp-field">
                                <label>Base Price (₱) <span className="mp-field-hint-inline">Displayed as starting price in catalog</span></label>
                                <input type="number" min="0" value={form.price} onChange={e => setForm(p=>({...p,price:e.target.value}))} placeholder="0" />
                            </div>
                            {isSimpleProduct && (
                                <div className="mp-field">
                                    <label>Initial Stock Quantity</label>
                                    <input type="number" min="0" step="1" value={form.stockQuantity} onChange={e => setForm(p=>({...p,stockQuantity:e.target.value}))} placeholder="0" />
                                    <p className="mp-field-hint">Optional. Set the initial stock level for this simple product.</p>
                                </div>
                            )}
                            <div className="mp-field">
                                <label>Status</label>
                                <select value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>

                        {showVariantFields && (
                            <>
                                <div className="mp-field-section-title">{form.subcategory} Details</div>
                                {isClothing && (
                                    <div className="mp-field">
                                        <label>Size Tier</label>
                                        <div className="mp-size-tier-btns">
                                            {['Adults','Kids'].map(tier => (
                                                <button key={tier} type="button"
                                                    className={`mp-size-tier-btn${form.sizeTier===tier?' mp-size-tier-btn--active':''}`}
                                                    onClick={() => setForm(p=>({...p,sizeTier:tier}))}>
                                                    {tier}
                                                    <span className="mp-size-tier-hint">{tier==='Adults'?'XXS – 6XL':'XS – 2XL'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {showColors && (
                                    <div className="mp-field">
                                        <label>Colors & Color Surcharges</label>
                                        <p className="mp-field-hint">Select colors available. Set a surcharge (₱) added on top of size price. White = ₱0 by default.</p>
                                        <div className="mp-color-presets">
                                            {COLOR_PRESETS.map(c => (
                                                <button key={c} type="button"
                                                    className={`mp-color-preset${form.colors.includes(c)?' mp-color-preset--selected':''}`}
                                                    onClick={() => form.colors.includes(c)?removeColor(c):addColor(c)} title={c}
                                                    style={{ background:COLOR_MAP[c]||'#ccc', border:c==='White'?'1.5px solid #ddd':'none' }} />
                                            ))}
                                        </div>
                                        <div className="mp-color-input-row">
                                            <input type="text" placeholder="Add custom color…" value={colorInput}
                                                onChange={e => setColorInput(e.target.value)}
                                                onKeyDown={e => e.key==='Enter'&&addColor()} />
                                            <button type="button" className="mp-color-add-btn" onClick={() => addColor()}>Add</button>
                                        </div>
                                        {form.colors.length > 0 && (
                                            <div className="mp-color-price-table">
                                                <div className="mp-color-price-header"><span>Color</span><span>Surcharge (₱)</span><span></span></div>
                                                {form.colors.map(c => (
                                                    <div key={c} className="mp-color-price-row">
                                                        <div className="mp-color-price-swatch-name">
                                                            <span className="mp-color-dot" style={{ background:COLOR_MAP[c]||'#ccc', border:c==='White'?'1px solid #ddd':'none', width:16,height:16,borderRadius:'50%',display:'inline-block',flexShrink:0 }} />
                                                            <span className="mp-color-price-name">{c}</span>
                                                        </div>
                                                        <input type="number" min="0" placeholder="0"
                                                            value={getColorPriceDisplayValue(c)}
                                                            onFocus={() => handleColorPriceFocus(c)}
                                                            onBlur={e => handleColorPriceBlur(c,e.target.value)}
                                                            onChange={e => handleColorPriceChange(c,e.target.value)}
                                                            className="mp-color-price-input" />
                                                        <button type="button" className="mp-color-price-remove" onClick={() => removeColor(c)}>×</button>
                                                    </div>
                                                ))}
                                                <p className="mp-field-hint" style={{ margin:'6px 0 0' }}>
                                                    💡 Surcharge is added to the size price. Example: XS = ₱120 + Black ₱30 = ₱150 total.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(isJersey||isPoloShirt) && (
                                    <div className="mp-field">
                                        <p className="mp-field-hint mp-field-hint--info">
                                            ℹ️ {form.subcategory} products do not use color options. Size and quantity selection only.
                                        </p>
                                    </div>
                                )}
                                {form.sizeTier ? (
                                    <div className="mp-field">
                                        <label>Available Sizes & Per-Size Pricing</label>
                                        <p className="mp-field-hint">
                                            Check the sizes this product offers and set the price for each.
                                            {isTShirt && <span className="mp-field-hint--warn"> T-shirt prices should not exceed ₱{TSHIRT_MAX_PRICE_WARN}.</span>}
                                        </p>
                                        {priceWarnings.length>0&&(
                                            <div className="mp-price-warning-banner">
                                                ⚠ Sizes exceeding ₱{TSHIRT_MAX_PRICE_WARN}: <strong>{priceWarnings.join(', ')}</strong>
                                            </div>
                                        )}
                                        <div className="mp-size-price-grid">
                                            <div className="mp-size-price-header"><span>Size</span><span>Available</span><span>Price (₱)</span></div>
                                            {sizesDisplay.map((display,idx) => {
                                                const key      = sizesKeys[idx];
                                                const isChecked = (form.availableSizes||[]).includes(key);
                                                const priceVal  = form.sizePrices?.[key];
                                                const isOver    = isTShirt&&isChecked&&priceVal!==null&&priceVal!==undefined&&Number(priceVal)>TSHIRT_MAX_PRICE_WARN;
                                                return (
                                                    <div key={key} className={`mp-size-price-row${isChecked?' mp-size-price-row--active':''}${isOver?' mp-size-price-row--warn':''}`}>
                                                        <span className="mp-size-price-label">{display}</span>
                                                        <input type="checkbox" checked={isChecked} onChange={() => toggleAvailableSize(key)} className="mp-size-price-check" />
                                                        <input type="number" min="0" disabled={!isChecked}
                                                            value={isChecked?(priceVal || ''):''}
                                                            placeholder={isChecked?'0':'—'}
                                                            onChange={e => handleSizePriceChange(key,e.target.value)}
                                                            className={`mp-size-price-input${isOver?' mp-size-price-input--warn':''}`} />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : isClothing && (
                                    <p className="mp-field-hint mp-field-hint--warn">⚠ Select a Size Tier above to configure per-size pricing.</p>
                                )}
                            </>
                        )}

                        {!showVariantFields && isClothing && (
                            <div className="mp-field">
                                <label>Size Tier</label>
                                <div className="mp-size-tier-btns">
                                    {['Adults','Kids'].map(tier => (
                                        <button key={tier} type="button"
                                            className={`mp-size-tier-btn${form.sizeTier===tier?' mp-size-tier-btn--active':''}`}
                                            onClick={() => setForm(p=>({...p,sizeTier:tier}))}>
                                            {tier}
                                            <span className="mp-size-tier-hint">{tier==='Adults'?'XXS – 6XL':'XS – 2XL'}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mp-field-section-title">Product Description</div>
                        <div className="mp-field">
                            <label>Description <span className="mp-field-hint-inline">Optional - displayed on product details page</span></label>
                            <p className="mp-field-hint">Use bullet points (• or -) to list features. Use **text** for highlighted key points. Plain text becomes intro paragraph.</p>
                            <textarea 
                                value={form.description} 
                                onChange={e => setForm(p=>({...p,description:e.target.value}))} 
                                placeholder="e.g. Premium quality cotton fabric.&#10;• Soft and breathable&#10;• Perfect for custom printing&#10;**Available in 9 sizes**"
                                className="mp-description-textarea"
                                rows="6"
                            />
                        </div>
                    </div>
                </div>

                <div className="mp-modal-footer">
                    <button className="mp-btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
                    <button className="mp-btn-primary" onClick={handleSubmit} disabled={uploading}>
                        {uploading ? 'Uploading…' : (isEdit ? 'Update Product' : 'Create Product')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Delete dialog ────────────────────────────────────────────────────────────
function DeleteDialog({ product, onClose, onConfirm }) {
    return (
        <div className="mp-modal-overlay" onClick={onClose}>
            <div className="mp-dialog" onClick={e => e.stopPropagation()}>
                <h3 className="mp-dialog-title">Delete Product</h3>
                <p className="mp-dialog-body">Are you sure you want to delete <strong>{product.name}</strong>? This cannot be undone.</p>
                <div className="mp-dialog-actions">
                    <button className="mp-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="mp-btn-danger" onClick={onConfirm}>Delete</button>
                </div>
            </div>
        </div>
    );
}

// ─── Category-grouped product table with independent pagination ───────────────
function CategoryProductTable({ categoryName, products, pageSize, setModal, setDeleteTarget }) {
    const [page, setPage] = useState(1);

    useEffect(() => { setPage(1); }, [pageSize, products.length]);

    const totalPages = Math.max(1, Math.ceil(products.length / pageSize));
    const paginated = products.slice((page - 1) * pageSize, page * pageSize);
    const hasAnySizeTier = products.some(p => p.sizeTier);
    const hasAnyColors = products.some(p => Array.isArray(p.colors) && p.colors.length > 0);
    const hasAnyAttrs = products.some(p => {
        const attrs = p.selectedAttributes || p.attributes;
        return attrs && typeof attrs === 'object' && Object.keys(attrs).length > 0;
    });

    // Compute dynamic column count
    let colCount = 7; // Image, Name, Subcategory, Stock, Price, Status, Actions
    if (hasAnySizeTier) colCount++;
    if (hasAnyColors) colCount++;
    if (hasAnyAttrs) colCount++;

    return (
        <div className="mp-category-block" style={{ marginBottom: '32px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px', color: '#111', borderBottom: '2px solid #eaeaea', paddingBottom: '8px' }}>
                {categoryName}
            </h3>
            <div className="mp-table-wrap">
                <table className="mp-table">
                    <thead>
                        <tr>
                            <th>Image</th>
                            <th>Product Name</th>
                            <th>Subcategory</th>
                            <th>Stock</th>
                            {hasAnyColors && <th>Colors</th>}
                            {hasAnySizeTier && <th>Size Tier</th>}
                            {hasAnyAttrs && <th>Attributes</th>}
                            <th>Base Price</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginated.length === 0 ? (
                            <tr><td colSpan={colCount} className="mp-empty">No products found.</td></tr>
                        ) : paginated.map(p => {
                            const totalStock = p.inventory && typeof p.inventory === 'object' && Object.keys(p.inventory).length > 0
                                ? getTotalStock(p.inventory)
                                : (typeof p.stockQuantity === 'number' ? p.stockQuantity : null);
                            const productAttrs = p.selectedAttributes || p.attributes;
                            const isRowClothing = p.category === CLOTHING_CATEGORY;
                            const isVariantHeavy = isRowClothing && p.colors.length > 0 && p.sizeTier;
                            return (
                                <tr key={p.id}>
                                    <td>
                                        <div className="mp-thumb-cell">
                                            {p.image
                                                ? <img src={p.image} alt={p.name} className="mp-thumb" />
                                                : <div className="mp-thumb-placeholder"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
                                            }
                                        </div>
                                    </td>
                                    <td className="mp-product-name">{p.name}</td>
                                    <td>{p.subcategory || '—'}</td>
                                    <td className="mp-stock">{totalStock !== null ? totalStock.toLocaleString() : <span className="mp-no-colors">—</span>}</td>
                                    {hasAnyColors && (
                                        <td>
                                            <div className="mp-color-dots">
                                                {isVariantHeavy ? (
                                                    NO_COLOR_SUBCATEGORIES.includes(p.subcategory) ? (
                                                        <span className="mp-no-colors mp-no-colors--label">N/A</span>
                                                    ) : p.colors.length > 0 ? (
                                                        <>
                                                            {p.colors.slice(0, 5).map(c => <ColorDot key={c} color={c} />)}
                                                            {p.colors.length > 5 && <span className="mp-more-colors">+{p.colors.length - 5}</span>}
                                                        </>
                                                    ) : (
                                                        <span className="mp-no-colors">—</span>
                                                    )
                                                ) : p.colors && p.colors.length > 0 ? (
                                                    <>
                                                        {p.colors.slice(0, 5).map(c => <ColorDot key={c} color={c} />)}
                                                        {p.colors.length > 5 && <span className="mp-more-colors">+{p.colors.length - 5}</span>}
                                                    </>
                                                ) : (
                                                    <span className="mp-no-colors">—</span>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                    {hasAnySizeTier && <td>{p.sizeTier || '—'}</td>}
                                    {hasAnyAttrs && (
                                        <td>
                                            {productAttrs && Object.keys(productAttrs).length > 0 ? (
                                                <DynamicAttributesDisplay attributes={productAttrs} />
                                            ) : '—'}
                                        </td>
                                    )}
                                    <td className="mp-price">₱{(p.price || 0).toLocaleString()}</td>
                                    <td><StatusBadge status={p.status} /></td>
                                    <td>
                                        <div className="mp-actions">
                                            <button className="mp-action-btn mp-action-btn--edit" onClick={() => setModal(p)} title="Edit">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                            </button>
                                            <button className="mp-action-btn mp-action-btn--delete" onClick={() => setDeleteTarget(p)} title="Delete">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="mp-pagination">
                <span className="mp-result-count">
                    Showing {products.length === 0 ? 0 : Math.min((page - 1) * pageSize + 1, products.length)}–{Math.min(page * pageSize, products.length)} of {products.length}
                </span>
                <button className="mp-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Previous</button>
                <span className="mp-page-divider">|</span>
                <button className="mp-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdminManageProducts() {
    const { products, loading:productsLoading, error:productsError, fetchProducts, createProductEntry, updateProductEntry, deleteProductEntry } = useProductManagement([]);
    const { categories, fetchCategories } = useCategoryManagement([]);
    const [attributes, setAttributes] = useState([]);

    const [search,         setSearch]        = useState('');
    const [pageSize,       setPageSize]      = useState(10);
    const [statusFilter,   setStatusFilter]  = useState('all');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterSubcat,   setFilterSubcat]  = useState('all');
    const [sortBy,         setSortBy]        = useState('name-asc');
    const [modal,          setModal]         = useState(null);
    const [deleteTarget,   setDeleteTarget]  = useState(null);
    const [actionError,    setActionError]   = useState(null);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        getAllAttributes().then((result) => {
            if (!result.error) setAttributes(result.attributes);
        });
    }, [fetchProducts, fetchCategories]);

    const categoryTree = useMemo(() => buildCategoryTree(categories), [categories]);
    const categoryNames = useMemo(() => categoryTree.map(c => c.name).filter(Boolean), [categoryTree]);
    const subcategoryEntities = useMemo(() => categoryTree.flatMap((main) => main.subcategories.map((sub) => ({ ...sub, parentName: main.name }))), [categoryTree]);

    const subcategoryMap = useMemo(() => {
        const map = {};
        categoryTree.forEach(c => { if (c.name) map[c.name] = Array.isArray(c.subcategories) ? c.subcategories.map((subcategory) => subcategory.name) : []; });
        return map;
    }, [categoryTree]);

    // ── Dynamic subcategory options based on selected category ────────────────
    const subcategoryOptions = useMemo(() => {
        if (filterCategory === 'all') {
            // All subcategories across all categories
            const all = new Set();
            categoryTree.forEach(c => (c.subcategories || []).forEach(s => all.add(s.name)));
            return [...all].sort();
        }
        return subcategoryMap[filterCategory] || [];
    }, [filterCategory, subcategoryMap, categoryTree]);

    const productsView = useMemo(() => products.map(p => ({
        ...p,
        name:         p.name || '',
        category:     p.category || '',
        image:        p.imageUrl || p.image || (Array.isArray(p.images) && p.images[0]) || null,
        imageUrl:     p.imageUrl || p.image || '',
        images:       Array.isArray(p.images) ? p.images : [],
        colors:       Array.isArray(p.colors) ? p.colors : [],
        colorPrices:  p.colorPrices || {},
        subcategory:  p.subcategory || '',
        sizeTier:     p.sizeTier || null,
        status:       p.status || 'inactive',
    })), [products]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        let list = productsView.filter(p => {
            const nameValue = p.name.toLowerCase();
            const categoryValue = (p.category || '').toLowerCase();
            const subcategoryValue = (p.subcategory || '').toLowerCase();
            const matchSearch = !q || nameValue.includes(q) || categoryValue.includes(q) || subcategoryValue.includes(q);
            const matchStatus = statusFilter==='all' || p.status===statusFilter;
            const matchCat    = filterCategory==='all' || p.category===filterCategory;
            const matchSubcat = filterSubcat==='all'   || p.subcategory===filterSubcat;
            return matchSearch && matchStatus && matchCat && matchSubcat;
        });

        // Sort
        list = [...list].sort((a, b) => {
            if (sortBy==='name-asc')  return a.name.localeCompare(b.name);
            if (sortBy==='name-desc') return b.name.localeCompare(a.name);
            return 0;
        });

        return list;
    }, [productsView, search, statusFilter, filterCategory, filterSubcat, sortBy]);

    const counts = useMemo(() => ({
        all:      productsView.length,
        active:   productsView.filter(p => p.status==='active').length,
        inactive: productsView.filter(p => p.status==='inactive').length,
    }), [productsView]);

    // Group filtered products by category
    const productsByCategory = useMemo(() => {
        const map = {};
        filtered.forEach(p => {
            const cat = p.category || 'Uncategorized';
            if (!map[cat]) map[cat] = [];
            map[cat].push(p);
        });
        return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
    }, [filtered]);

    // Reset subcategory filter when category changes
    useEffect(() => { setFilterSubcat('all'); }, [filterCategory]);

    const hasActiveFilters = filterCategory!=='all' || filterSubcat!=='all' || sortBy!=='name-asc';

    const handleSave = async (data) => {
        setActionError(null);
        const selectedAttributes = Object.keys(data.selectedAttributes || {}).length > 0
            ? data.selectedAttributes
            : undefined;
        const payload = {
            name:           data.name,
            category:       data.category,
            subcategory:    data.subcategory || null,
            price:          data.price,
            status:         data.status,
            sizeTier:       data.category === CLOTHING_CATEGORY ? (data.sizeTier || null) : null,
            colors:         data.colors   || [],
            colorPrices:    data.colorPrices || {},
            imageUrl:       data.imageUrl || '',
            images:         data.images   || [],
            ...(data.stockQuantity !== undefined && { stockQuantity: data.stockQuantity }),
            description:    data.description || '',
            submenuTags:    data.submenuTags || [],
            ...(data.availableSizes!==undefined && { availableSizes:data.availableSizes }),
            ...(data.sizePrices     !==undefined && { sizePrices:    data.sizePrices }),
            ...(selectedAttributes!==undefined && { selectedAttributes }),
        };
        console.debug('[AdminManageProducts] saving product payload', {
            mode: modal?.id ? 'update' : 'create',
            productId: modal?.id || null,
            hasDescription: Boolean(payload.description),
            descriptionLength: payload.description.length,
            descriptionPreview: payload.description.slice(0, 80),
        });
        if (modal?.id) {
            const result = await updateProductEntry(modal.id, payload);
            if (result.error) {
                console.error('[AdminManageProducts] product update failed', result.error);
                setActionError(result.error);
                return;
            }
        } else {
            const result = await createProductEntry(payload);
            if (result.error) {
                console.error('[AdminManageProducts] product create failed', result.error);
                setActionError(result.error);
                return;
            }
        }
        setModal(null);
    };

    const handleDelete = async () => {
        setActionError(null);
        const result = await deleteProductEntry(deleteTarget.id);
        if (result.error) { setActionError(result.error); return; }
        setDeleteTarget(null);
    };

    return (
        <div className="mp-page">
            <ProductTabBar active="Manage Products" />

            {productsLoading && <div className="mp-info-banner">Loading products…</div>}
            {(productsError||actionError) && <div className="mp-info-banner mp-info-banner--error">{productsError||actionError}</div>}

            {/* Toolbar */}
            <div className="mp-toolbar">
                <div className="mp-toolbar-left">
                    <div className="mp-entries-label">
                        Show
                        <select className="mp-entries-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); }}>
                            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        entries
                    </div>
                </div>
                <div className="mp-toolbar-right">
                    <div className="mp-search-wrap">
                        <input type="text" className="mp-search" placeholder="Search products…"
                            value={search} onChange={e => { setSearch(e.target.value); }} />
                        <svg className="mp-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </div>
                    <button className="mp-btn-add" onClick={() => setModal('add')}>
                        Add New Product
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                </div>
            </div>

            {/* Status filter tabs */}
            <div className="mp-filter-tabs">
                {[['all','All'],['active','Active'],['inactive','Inactive']].map(([val,label]) => (
                    <button key={val} className={`mp-filter-tab${statusFilter===val?' mp-filter-tab--active':''}`}
                        onClick={() => { setStatusFilter(val); }}>
                        {label}
                        <span className="mp-tab-count">{counts[val]}</span>
                    </button>
                ))}
            </div>

            {/* ── Category / Subcategory filter + Sort bar (matches AdminManageInventory style) ── */}
            <div className="mi-filter-bar">
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Category</label>
                    <select className="mi-filter-select" value={filterCategory}
                        onChange={e => { setFilterCategory(e.target.value); }}>
                        <option value="all">All Categories</option>
                        {categoryNames.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Subcategory</label>
                    <select className="mi-filter-select" value={filterSubcat}
                        onChange={e => { setFilterSubcat(e.target.value); }}
                        disabled={subcategoryOptions.length === 0}>
                        <option value="all">All Subcategories</option>
                        {subcategoryOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div className="mi-filter-group">
                    <label className="mi-filter-label">Sort By</label>
                    <select className="mi-filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value="name-asc">Name A–Z</option>
                        <option value="name-desc">Name Z–A</option>
                    </select>
                </div>
                {hasActiveFilters && (
                    <button className="mi-filter-clear"
                        onClick={() => { setFilterCategory('all'); setFilterSubcat('all'); setSortBy('name-asc'); }}>
                        Clear filters
                    </button>
                )}
            </div>

            {/* Category-grouped tables */}
            {productsLoading ? null : productsByCategory.length === 0 ? (
                <div className="mp-table-wrap">
                    <table className="mp-table">
                        <tbody><tr><td colSpan={10} className="mp-empty">No products found.</td></tr></tbody>
                    </table>
                </div>
            ) : productsByCategory.map(([categoryName, categoryProducts]) => (
                <CategoryProductTable
                    key={categoryName}
                    categoryName={categoryName}
                    products={categoryProducts}
                    pageSize={pageSize}
                    setModal={setModal}
                    setDeleteTarget={setDeleteTarget}
                />
            ))}

            {modal && modal!=='add' && (
                <ProductModal product={modal} onClose={() => setModal(null)} onSave={handleSave} categories={categoryNames} subcategoryMap={subcategoryMap} subcategoryEntities={subcategoryEntities} attributes={attributes} />
            )}
            {modal==='add' && (
                <ProductModal product={null} onClose={() => setModal(null)} onSave={handleSave} categories={categoryNames} subcategoryMap={subcategoryMap} subcategoryEntities={subcategoryEntities} attributes={attributes} />
            )}
            {deleteTarget && <DeleteDialog product={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />}
        </div>
    );
}
